import React from 'react';
import {
  Select as MuiSelect,
  MenuItem,
  FormControl,
  InputLabel,
  SelectChangeEvent,
  MenuProps as MuiMenuProps,
  ListSubheader,
  useTheme,
} from '@mui/material';
import { deepmerge } from '@mui/utils';
import { PounceStyleProps, extractPounceProps, mapPouncePropsToSx } from '../../utils';
import { Box } from '../Box';
import { IconButton } from '../IconButton';
import { Icon } from '../Icon';
import { Flex } from '../Flex';
import { AbstractButton } from '../AbstractButton';
import { Spinner } from '../Spinner';
import { SearchableCombobox } from './SearchableCombobox';
import { typographyTokens } from '../../theme/typography-tokens';

export type ComboboxProps<T> = {
  /** Callback when the selection changes */
  onChange: (value: T | null) => void;

  /** A list of entries that the dropdown will have as options */
  items: T[];

  /** The id of the combobox */
  id?: string;

  /** The label that is associated with this combobox */
  label: string;

  /** Whether the label should get visually hidden */
  hideLabel?: boolean;

  /**
   * A function that converts an item to a string. This is the value that the dropdown will
   * expose to the user. This is also the value that the dropdown will use internally for comparisons
   * so it should be unique (which should be by default since you wouldn't want to expose duplicate
   * values to the user) */
  itemToString?: (item: T) => string;

  /**
   * A function used to group the Menu Items.
   */
  itemToGroup?: (item: T) => string;

  /**
   * This variable is used to render additional content at the end of the input (useful for icons)
   */
  inputEndContent?: React.ReactNode;

  /**
   * A function that accepts an item as a parameteer and returns `true` if the item should be
   * disabled or `false` otherwise. Defaults to `() => false`.
   */
  disableItem?: (item: T) => boolean;

  /** Whether the combobox has an invalid value */
  invalid?: boolean;

  /** Whether the combobox is in a loading state. This is useful when the `items` are coming asynchronously */
  loading?: boolean;

  /** Whether the combobox is required or not */
  required?: boolean;

  /** Whether the combobox is disabled or not */
  disabled?: boolean;

  /** A placeholder that's visible when the user focuses on the Combobox */
  placeholder?: string;

  /** A handler for the blur event on the Combobox's input */
  onBlur?: React.FocusEventHandler<HTMLInputElement>;

  /** The name passed to the underlying input */
  name?: string;

  /** @ignore */
  hidden?: boolean;

  /**
   * The value of the item that is currently selected. The component is a controlled one,
   * so the the selected value should be provided explicitly to the dropdown
   * */
  value: T | null;

  /** Whether the Combobox should have an input to search results */
  searchable?: boolean;

  /**
   * Render the dropdown menu inline instead of portaling it to <body>. Use when
   * the Combobox lives inside another popover/positioned container so the menu
   * anchors and layers within that container instead of fighting its z-index.
   */
  disablePortal?: boolean;

  /**
   * The maximum number of results that the MultiCombobox should show. Default value is
   * `undefined` to display which displays all of them.
   * */
  maxResults?: number;

  /** The maximum height (in pixels) of the Combobox dropdown. Defaults to 300. */
  maxHeight?: number;

  /** The maximum width (in pixels) of the Combobox dropdown. Defaults to 800. */
  maxWidth?: number;

  /** Whether a clear selection control should be available to the user */
  showClearSelectionControl?: boolean;

  /**
   * A function that renders items in the dropdown. If not provided, the itemToString function will be used.
   */
  renderMenuItem?: (item: T) => React.ReactNode;

  /** Whether the Combobox should be open by default */
  initialIsOpen?: boolean;

  /**
   * Custom text to display when no item is selected. If not provided, shows placeholder/empty as usual.
   */
  emptyText?: string;
} & PounceStyleProps;

/**
 * A simple Combobox can be thought of as a typical `<select>` component. Whenerever you would
 * use a normal select, you should now pass the `<Combobox>` component.
 *
 * This component maintains API compatibility with the original pounce Combobox while using
 * MUI's Select component internally for better accessibility and integration.
 */
function ComboboxComponent<Item>(
  {
    onChange,
    onBlur,
    value,
    items,
    searchable = false,
    label = '',
    hideLabel,
    disabled = false,
    loading = false,
    disableItem = () => false,
    itemToString = item => String(item),
    itemToGroup,
    maxHeight = 300,
    maxWidth = 800,
    maxResults,
    invalid,
    required,
    hidden,
    inputEndContent,
    showClearSelectionControl = true,
    renderMenuItem,
    initialIsOpen = false,
    emptyText,
    id,
    name,
    placeholder,
    disablePortal,
    sx,
    ...rest
  }: ComboboxProps<Item>,
  ref: React.Ref<HTMLDivElement>,
): React.ReactElement<ComboboxProps<Item>> {
  const { pounceProps, otherProps } = extractPounceProps(rest);
  const pounceStyles = mapPouncePropsToSx(pounceProps);
  const theme = useTheme();
  const componentTokens = theme.palette?.componentTokens;
  const tokens = theme.palette?.tokens;

  const [open, setOpen] = React.useState(initialIsOpen);
  const [searchTerm, setSearchTerm] = React.useState('');

  // Use open state as indicator of focus - when select is open or was just opened, it's focused
  // This matches MUI Select behavior where clicking/focusing opens the menu
  const isFocused = open;

  // Convert item to a string with fallback to emptyText or empty string
  const safeItemToString = React.useCallback(
    (item: Item | null) => (item != null ? itemToString(item) : emptyText || ''),
    [itemToString, emptyText],
  );

  // Find the string value for the current selection
  const selectedValue = value != null ? safeItemToString(value) : '';

  // Filter items based on search term (only for searchable combobox)
  const filteredItems = React.useMemo(() => {
    let result = items;

    // Apply search filter if searchable and there's a search term
    if (searchable && searchTerm) {
      result = items.filter(item => {
        const itemString = itemToGroup
          ? `${itemToGroup(item)}${itemToString(item)}`
          : itemToString(item);
        return itemString.toLowerCase().includes(searchTerm.toLowerCase());
      });
    }

    // Apply max results limit
    if (maxResults) {
      result = result.slice(0, maxResults);
    }

    return result;
  }, [items, searchTerm, searchable, maxResults, itemToString, itemToGroup]);

  // Group items if itemToGroup is provided
  const groupedItems = React.useMemo(() => {
    if (!itemToGroup) {
      return null;
    }

    const groups = new Map<string, Item[]>();
    filteredItems.forEach(item => {
      const group = itemToGroup(item);
      if (!groups.has(group)) {
        groups.set(group, []);
      }
      const groupItems = groups.get(group);
      if (groupItems) {
        groupItems.push(item);
      }
    });

    return groups;
  }, [filteredItems, itemToGroup]);

  const handleChange = (event: SelectChangeEvent<string>) => {
    event.stopPropagation();
    const selectedString = event.target.value;

    if (selectedString === '') {
      onChange(null);
      return;
    }

    // Find the item that matches the selected string value
    const selectedItem = items.find(item => itemToString(item) === selectedString);
    onChange(selectedItem ?? null);
  };

  const handleClearSelection = (event: React.MouseEvent) => {
    event.stopPropagation();
    onChange(null);
    setOpen(false); // Close the menu after clearing selection
    setSearchTerm(''); // Clear search term if any
  };

  const handleOpen = () => {
    setOpen(true);
    if (searchable) {
      setSearchTerm('');
    }
  };

  const handleClose = () => {
    setOpen(false);
    setSearchTerm('');

    // Blur the select when closing to remove focus styling and trigger onBlur
    // This is a workaround for MUI Select not blurring properly
    // See: https://github.com/mui/material-ui/issues/26076
    setTimeout(() => {
      (document.activeElement as HTMLElement)?.blur();
    }, 0);
  };

  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    // Only call onBlur if provided - MUI Select handles blur internally
    if (onBlur) {
      onBlur(event);
    }
  };

  const menuProps: Partial<MuiMenuProps> = {
    // Enable circular keyboard navigation (wrap around at top/bottom) - default behavior in MUI
    variant: 'menu',
    // Render inline (vs portaled to <body>) when requested, so the menu anchors
    // and layers within an enclosing popover instead of fighting its z-index.
    disablePortal,
    PaperProps: {
      sx: {
        maxHeight,
        maxWidth,
        mt: '8px',
        overflow: 'hidden', // Clip overflow - MenuList handles internal scrolling
        borderRadius: '6px',
        padding: 0,
      },
    },
    MenuListProps: {
      // autoFocus must be conditional on open state to work on subsequent opens
      autoFocus: open,
      // disableListWrap: false is the default - enables circular navigation
      disableListWrap: false,
      sx: {
        maxHeight,
        overflow: 'auto', // Enable scrolling on MenuList (the actual scrollable container)
        borderRadius: '6px',
        padding: '6px',
      },
    },
  };

  // Render menu items with optional grouping
  const renderMenuItems = () => {
    if (groupedItems) {
      // Render grouped items
      const groupElements: React.ReactNode[] = [];
      groupedItems.forEach((groupItems, groupName) => {
        groupElements.push(<ListSubheader key={`group-${groupName}`}>{groupName}</ListSubheader>);
        groupItems.forEach(item => {
          const itemString = itemToString(item);
          const isDisabled = disableItem(item);
          groupElements.push(
            <MenuItem key={itemString} value={itemString} disabled={isDisabled}>
              {renderMenuItem ? renderMenuItem(item) : itemString}
            </MenuItem>,
          );
        });
      });
      return groupElements;
    }

    // Render flat list
    return filteredItems.map(item => {
      const itemString = itemToString(item);
      const isDisabled = disableItem(item);
      return (
        <MenuItem key={itemString} value={itemString} disabled={isDisabled}>
          {renderMenuItem ? renderMenuItem(item) : itemString}
        </MenuItem>
      );
    });
  };

  // Combine pounce styles with additional FormControl styles
  const combinedSx = deepmerge(pounceStyles, sx);

  // Render searchable combobox using Autocomplete (allows typing in main input)
  if (searchable) {
    return (
      <SearchableCombobox
        value={value}
        onChange={onChange}
        onOpen={handleOpen}
        onClose={handleClose}
        onBlur={handleBlur}
        open={open}
        items={items}
        itemToString={itemToString}
        itemToGroup={itemToGroup}
        disableItem={disableItem}
        renderMenuItem={renderMenuItem}
        loading={loading}
        disabled={disabled}
        invalid={invalid}
        required={required}
        label={label}
        hideLabel={hideLabel}
        placeholder={placeholder}
        emptyText={emptyText}
        name={name}
        maxResults={maxResults}
        maxHeight={maxHeight}
        maxWidth={maxWidth}
        showClearSelectionControl={showClearSelectionControl}
        disablePortal={disablePortal}
        inputEndContent={inputEndContent}
        combinedSx={combinedSx}
        otherProps={otherProps}
      />
    );
  }

  // Render non-searchable combobox using Select
  return (
    <FormControl
      ref={ref}
      fullWidth
      error={invalid}
      disabled={disabled}
      required={required}
      hidden={hidden}
      sx={combinedSx}
    >
      {!hideLabel && (
        <InputLabel id={`${id}-label`} shrink={value != null || !!emptyText || isFocused}>
          {label}
        </InputLabel>
      )}
      <MuiSelect
        labelId={`${id}-label`}
        id={id}
        name={name}
        value={selectedValue}
        onChange={handleChange}
        onOpen={handleOpen}
        onClose={handleClose}
        onBlur={handleBlur}
        open={open}
        displayEmpty
        label={hideLabel ? '' : label}
        inputProps={
          hideLabel
            ? {
                'aria-label': label,
              }
            : undefined
        }
        MenuProps={menuProps}
        IconComponent={() => null} // Hide default icon, we'll use custom one
        renderValue={selected => {
          if (selected === '' || selected === null || selected === undefined) {
            // Only show placeholder when focused or when there's emptyText
            // This matches original pounce behavior where placeholder appears on focus
            if (isFocused || !!emptyText) {
              return (
                <Box
                  as="span"
                  sx={{
                    opacity: isFocused && !emptyText ? 0.3 : 1,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {placeholder || emptyText || ''}
                </Box>
              );
            }
            // Return empty string when not focused and no value - label will be visible
            return '';
          }
          return selected as string;
        }}
        sx={{
          '& .MuiSelect-select': {
            pr: inputEndContent ? '46px' : '32px', // Account for custom icon button
          },
          // Override MUI's default disabled opacity to make text visible
          '&.Mui-disabled': {
            opacity: '0.4 !important',
          },
          '& .MuiSelect-select.Mui-disabled': {
            opacity: '1 !important',
            WebkitTextFillColor: 'currentColor',
          },
        }}
        {...otherProps}
      >
        {/* Clear selection option */}
        {showClearSelectionControl && value !== null && value !== undefined && (
          <Box
            sx={{
              position: 'sticky',
              top: 0,
              zIndex: 1,
              listStyle: 'none',
              transform: 'translateY(-6px)', // Required to counter the padding from the paper element
              backgroundColor:
                componentTokens?.selectMenuItems?.color?.item?.background?.containerBase,
            }}
          >
            <AbstractButton
              onClick={handleClearSelection}
              sx={{
                width: '100%',
                fontSize: typographyTokens.fontSizes.xs,
                fontWeight: typographyTokens.fontWeights.medium,
                justifyContent: 'start',
                color: tokens?.content.text.accent[3].base,
                '&:hover': { textDecoration: 'underline' },
                pt: '12px',
                pb: '6px',
                px: '12px',
              }}
            >
              <Flex as="span" align="center" justify="start" gap={1}>
                <Icon type="close-circle" size="small" color="inherit" />
                <Box as="span">Clear</Box>
              </Flex>
            </AbstractButton>
          </Box>
        )}

        {/* Render menu items */}
        {renderMenuItems()}
      </MuiSelect>

      {/* Custom icon button positioned absolutely */}
      <Box
        sx={{
          position: 'absolute',
          top: hideLabel ? '50%' : 'calc(50%)', // Account for label height
          right: '8px',
          transform: 'translateY(-50%)',
          display: 'flex',
          alignItems: 'center',
          opacity: disabled ? 0.4 : 1,
        }}
      >
        {loading ? (
          <Spinner size="small" sx={{ mr: 2 }} />
        ) : (
          <Flex align="center" spacing={0}>
            {inputEndContent && <Box>{inputEndContent}</Box>}
            <IconButton
              tabIndex={-1}
              icon={open ? 'caret-up' : 'caret-down'}
              size="medium"
              aria-label="Toggle Menu"
              disabled={disabled}
              variant="ghost"
              sx={{ pointerEvents: 'auto' }}
              onClick={() => (open ? handleClose() : handleOpen())}
            />
          </Flex>
        )}
      </Box>
    </FormControl>
  );
}

// Type-safe memo wrapper to preserve generic type parameter
export const Combobox = React.memo(React.forwardRef(ComboboxComponent)) as <T>(
  props: ComboboxProps<T> & { ref?: React.Ref<HTMLDivElement> },
) => React.ReactElement;
